// Live exchange rates for every money figure the app renders.
//
// CURRENCIES in constants/data.js used to carry a fixed `rate` per currency
// (ILS 3.73, EUR 0.92, GBP 0.79), and fmt() and <Money> multiplied every
// dollar value by it. The live USD/ILS was 3.03 the day this was written: a
// user who picked shekels saw a portfolio, and a P&L, 23% too large, with no
// marker anywhere. The fixed numbers are gone. A rate now comes from a feed
// with a date, or it does not exist — and then the figure is "—".
//
// ONE source for all three (2026-09-26): Yahoo Finance through marketdata
// (15-min cache, each rate stamped). If any one of the three is missing, all
// three come from ExchangeRate-API instead (daily, dated) · never a mix, so no
// two figures on one screen are converted at rates from different places.
//   USD · 1, by definition.

// One module-level store, because fmt() is a plain function called inside
// render and cannot take a hook. Components that must re-render on a rate
// change call useFxRates(); App does, and since it renders the whole tree,
// one subscription there is enough for every fmt() call below it. <Money>
// subscribes on its own as well so it is correct wherever it is mounted.

import { useEffect, useState } from "react";
import { fetchMarketData } from "./marketdata";
import { fetchExchangeRates } from "./finnhub";

const REFRESH_MS = 15 * 60 * 1000;

// { rate, date, source } per code, or null when no feed has answered.
let rates = { USD: { rate: 1, date: null, source: null }, ILS: null, EUR: null, GBP: null };
const subscribers = new Set();
let timer = null;
// Whether the first load has finished, with rates or without: the ticker tape
// waits for it before it starts to scroll (TickerTape.jsx).
let answered = false;
export const fxAnswered = () => answered;

export function getFx(cur) {
  return rates[cur] ?? null;
}

function publish() {
  for (const fn of subscribers) fn(rates);
}

const ok = (n) => Number.isFinite(n) && n > 0;

async function load() {
  const next = { ...rates };

  let md = null;
  try { md = await fetchMarketData(); } catch { /* handled below */ }
  const y = k => (ok(md?.[k]?.value) ? md[k] : null);
  const ils = y("usdIls"), eur = y("eurUsd"), gbp = y("gbpUsd");
  if (ils && eur && gbp) {
    // `at` is the moment the quote was stamped, so a screen can print the hour.
    const live = (q, rate) => ({ rate, date: q.date || null, at: q.quote_ts || null, source: "Yahoo Finance · כל 15 דקות" });
    next.ILS = live(ils, ils.value);
    next.EUR = live(eur, 1 / eur.value);   // EUR/USD is quoted dollars per euro
    next.GBP = live(gbp, 1 / gbp.value);
    next.ILS.change_pct = Number.isFinite(ils.change_pct) ? ils.change_pct : null;
  } else {
    let fx = null;
    try { fx = await fetchExchangeRates(); } catch { /* handled below */ }
    if (fx?.rates) {
      const date = fx.time_last_update_unix
        ? new Date(fx.time_last_update_unix * 1000).toISOString().slice(0, 10)
        : null;
      const at = fx.time_last_update_unix ? new Date(fx.time_last_update_unix * 1000).toISOString() : null;
      const daily = (rate) => ({ rate, date, at, source: "ExchangeRate-API · עדכון יומי" });
      next.ILS = ok(fx.rates.ILS) ? daily(fx.rates.ILS) : null;
      next.EUR = ok(fx.rates.EUR) ? daily(fx.rates.EUR) : null;
      next.GBP = ok(fx.rates.GBP) ? daily(fx.rates.GBP) : null;
    }
  }

  rates = next;
  answered = true;
  publish();
}

function start() {
  if (timer) return;
  load();
  timer = setInterval(load, REFRESH_MS);
}

export function useFxRates() {
  const [snapshot, setSnapshot] = useState(rates);
  useEffect(() => {
    start();
    subscribers.add(setSnapshot);
    setSnapshot(rates);
    return () => { subscribers.delete(setSnapshot); };
  }, []);
  return snapshot;
}
